/* Acrehand — the small amount of behaviour the brand sections need.
   Rails fall back to a plain horizontal scroll if this never runs, and the
   reveal is opt-in via the .ac-js class below, so a failure here can never
   leave a section invisible. */
(function () {
  'use strict';

  document.documentElement.classList.add('ac-js');

  function wireRails(root) {
    (root || document).querySelectorAll('[data-ac-rail]').forEach(function (track) {
      if (track.dataset.acRailReady === '1') return;
      track.dataset.acRailReady = '1';

      var section = track.closest('.ac-rail');
      if (!section) return;

      var prev = section.querySelector('[data-ac-rail-prev]');
      var next = section.querySelector('[data-ac-rail-next]');

      function step() {
        var card = track.querySelector('.ac-card');
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* A shoppable dot is a plain link, so a mouse needs nothing from us. On a
     touch screen there is no hover: the first tap opens the price, a second
     tap follows the link. */
  function wireSpots(root) {
    var touch = window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (!touch) return;

    (root || document).querySelectorAll('.ac-spot').forEach(function (spot) {
      if (spot.dataset.acSpotReady === '1') return;
      spot.dataset.acSpotReady = '1';
      spot.setAttribute('aria-expanded', 'false');

      spot.addEventListener('click', function (e) {
        if (spot.getAttribute('aria-expanded') === 'true') return;
        e.preventDefault();
        var shot = spot.closest('.ac-shoppable');
        if (shot) {
          shot.querySelectorAll('.ac-spot[aria-expanded="true"]').forEach(function (other) {
            other.setAttribute('aria-expanded', 'false');
          });
        }
        spot.setAttribute('aria-expanded', 'true');
      });
    });
  }

  /* Inside a cover-cropped panel the photograph is wider or taller than the
     box, so a dot placed as a percentage of the box misses its frame. Work
     out the rectangle the image actually paints into and lay the dots out
     against that instead. If this never runs the dots still sit on the
     photograph, just less precisely. */
  function wireCover(root) {
    var layers = (root || document).querySelectorAll('.ac-shoppable[data-ac-cover]');
    if (!layers.length) return;

    function place(layer) {
      var img = layer.querySelector('img');
      var spots = layer.querySelectorAll('.ac-spot');
      if (!img || !spots.length || !img.naturalWidth) return;

      var boxW = layer.clientWidth;
      var boxH = layer.clientHeight;
      if (!boxW || !boxH) return;

      var scale = Math.max(boxW / img.naturalWidth, boxH / img.naturalHeight);
      var paintedW = img.naturalWidth * scale;
      var paintedH = img.naturalHeight * scale;
      /* object-position defaults to 50% 50%. */
      var offsetX = (boxW - paintedW) / 2;
      var offsetY = (boxH - paintedH) / 2;

      spots.forEach(function (spot) {
        var box = spot.dataset.acBox;
        if (!box) {
          box = [
            parseFloat(spot.style.left) || 0,
            parseFloat(spot.style.top) || 0,
            parseFloat(spot.style.width) || 0,
            parseFloat(spot.style.height) || 0
          ].join(',');
          spot.dataset.acBox = box;
        }
        var n = box.split(',').map(Number);
        spot.style.left = offsetX + (n[0] / 100) * paintedW + 'px';
        spot.style.top = offsetY + (n[1] / 100) * paintedH + 'px';
        spot.style.width = (n[2] / 100) * paintedW + 'px';
        spot.style.height = (n[3] / 100) * paintedH + 'px';
      });
    }

    layers.forEach(function (layer) {
      var img = layer.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', function () { place(layer); });
      place(layer);

      if (layer.dataset.acCoverReady === '1') return;
      layer.dataset.acCoverReady = '1';
      if ('ResizeObserver' in window) {
        new ResizeObserver(function () { place(layer); }).observe(layer);
      } else {
        window.addEventListener('resize', function () { place(layer); });
      }
    });
  }

  /* The frame preview. The file is read as a local object URL, so the
     photograph is never uploaded anywhere — which is what the copy promises. */
  function wireDemo(root) {
    (root || document).querySelectorAll('[data-ac-demo]').forEach(function (demo) {
      if (demo.dataset.acDemoReady === '1') return;
      demo.dataset.acDemoReady = '1';

      var scope = demo.closest('.ac-upload') || document;
      var open = demo.querySelector('[data-ac-demo-open]');
      var file = scope.querySelector('[data-ac-demo-file]');
      var state = scope.querySelector('[data-ac-demo-state]');
      var url = null;

      scope.querySelectorAll('[data-ac-wood]').forEach(function (button) {
        button.addEventListener('click', function () {
          scope.querySelectorAll('[data-ac-wood]').forEach(function (other) {
            other.setAttribute('aria-pressed', String(other === button));
          });
          demo.dataset.wood = button.dataset.acWood;
        });
      });

      if (!file || !open) return;

      file.addEventListener('change', function () {
        var chosen = this.files && this.files[0];
        if (!chosen) return;

        if (url) URL.revokeObjectURL(url);
        url = URL.createObjectURL(chosen);

        var img = new Image();
        img.alt = 'Your photograph shown inside the frame';
        img.onload = function () {
          open.textContent = '';
          open.appendChild(img);
          if (state && state.dataset.full) state.textContent = state.dataset.full;
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          url = null;
        };
        img.src = url;
      });
    });
  }

  function wireReveal(root) {
    var targets = (root || document).querySelectorAll('.ac-sec:not([data-ac-seen])');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.setAttribute('data-ac-seen', '1'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-ac-seen', '1');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
    );
    targets.forEach(function (el) { io.observe(el); });
  }

  function init(root) {
    wireRails(root);
    wireCover(root);
    wireSpots(root);
    wireDemo(root);
    wireReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* The theme editor swaps section markup in place. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
